import { z } from 'zod';
import { ClientDeletionStep, ClientOrgStatus, InvitationEmailStatus } from '@hangar/shared';
import { nstr, str } from './schemaPrimitives';

/* ---------- Members, invitations and client orgs (SPEC §17.5) ---------- */
/* Roles stay strings so a role the console does not know yet still renders. */

/** A person as the members views show them (api/openapi.yaml `UserSummary`). */
export const MemberUser = z.object({
  id: str,
  email: str,
  name: nstr,
  avatarUrl: nstr.optional(),
  isExternal: z.boolean().default(false),
  company: nstr.optional(),
});
export type MemberUser = z.infer<typeof MemberUser>;

export const Member = z.object({
  user: MemberUser,
  role: str,
  expiresAt: nstr,
  createdAt: str,
  lastLoginAt: nstr,
  projectRoles: z.array(z.object({ projectId: str, projectSlug: str.optional(), role: str, expiresAt: nstr })).default([]),
});
export type Member = z.infer<typeof Member>;
export const MemberList = z.object({ data: z.array(Member), nextCursor: z.string().nullable() });

export const Invitation = z.object({
  id: str,
  email: str,
  name: nstr,
  company: nstr,
  isExternal: z.boolean(),
  orgRole: str,
  projectRoles: z.array(z.object({ projectId: str, projectSlug: str.optional(), role: str })).default([]),
  expiresAt: nstr,
  /** `pending` · `accepted` · `revoked` · `expired`. */
  status: str,
  linkExpiresAt: str,
  invitedBy: MemberUser.nullable(),
  acceptedAt: nstr,
  createdAt: str,
  lastSentAt: nstr.optional(),
  /** What became of the last email (`sent`, `sign_in_sent`, `failed`, `bounced`); null on older invitations, absent from an older API. */
  emailStatus: InvitationEmailStatus.nullable().optional(),
});
export type Invitation = z.infer<typeof Invitation>;
export const InvitationList = z.object({ data: z.array(Invitation), nextCursor: z.string().nullable() });

export const InvitationAccepted = z.object({ org: z.object({ id: str, slug: str, name: str }), orgRole: str });
export type InvitationAccepted = z.infer<typeof InvitationAccepted>;

/** The external names of a client's provider accounts (`null` = not set up), SPEC decision 19. */
export const ClientOrgProviders = z.object({ eas: z.string().nullable(), bitrise: z.string().nullable() });
export type ClientOrgProviders = z.infer<typeof ClientOrgProviders>;
export const ProviderSetupStep = z.object({
  status: z.enum(['ready', 'created', 'not_configured', 'failed']),
  account: str.optional(),
  message: str.optional(),
});
export type ProviderSetupStep = z.infer<typeof ProviderSetupStep>;
/** `POST /platform/orgs/{orgId}/providers` and the `providers` of a new client. */
export const ProviderSetup = z.object({ eas: ProviderSetupStep, bitrise: ProviderSetupStep });
export type ProviderSetup = z.infer<typeof ProviderSetup>;

export const ClientOrg = z.object({
  id: str,
  slug: str,
  name: str,
  emailDomains: z.array(str).default([]),
  createdAt: str,
  memberCount: z.number(),
  openInvitations: z.number(),
  providers: ClientOrgProviders.default({ eas: null, bitrise: null }),
  /** Client lifecycle (SPEC §17.6); absent from an older API, which only lists active clients. */
  status: ClientOrgStatus.default('active'),
  /** Why and when it was suspended (operators only). */
  suspension: z.object({ at: str, reason: str }).nullable().default(null),
  /** The steps of a deletion that started (running, or failed and resumable). */
  deletion: z.object({ steps: z.array(ClientDeletionStep) }).nullable().default(null),
});
export type ClientOrg = z.infer<typeof ClientOrg>;
/** `POST /platform/orgs/{orgId}/deletion`: the steps, and the org while it still exists (`null` once deleted). */
export const ClientOrgDeletion = z.object({ org: ClientOrg.nullable(), steps: z.array(ClientDeletionStep) });
export type ClientOrgDeletion = z.infer<typeof ClientOrgDeletion>;
export const ClientOrgList = z.object({ data: z.array(ClientOrg), nextCursor: z.string().nullable() });
export const ClientOrgCreated = z.object({ org: ClientOrg, invitation: Invitation, providers: ProviderSetup.optional() });
export type ClientOrgCreated = z.infer<typeof ClientOrgCreated>;
